import { Box } from '@hivebrite/design-system/Box';

import { type Following } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/types';

import {
  type DropdownData,
  type GroupHeaderButtonsProperties,
} from './getGroupHeaderButtons';

const notificationOptions = [
  {
    ariaLabel: s__('Accessibility|Turn OFF notifications'),
    label: __('None'),
    type: 'none',
  },
  {
    ariaLabel: s__('Accessibility|Set Instant Notifications'),
    label: __('Instant'),
    type: 'instant',
  },
  {
    ariaLabel: s__('Accessibility|Set Daily Notifications'),
    label: __('Daily'),
    type: 'daily',
  },
  {
    ariaLabel: s__('Accessibility|Set Weekly Notifications'),
    label: __('Weekly'),
    type: 'weekly',
  },
];

type GetMemberActionsButtonProperties = Omit<
  GroupHeaderButtonsProperties,
  'topic'
> & {
  topicClosed: boolean;
};

export const getMemberActionsButton = ({
  following,
  unfollowTopic,
  updateSettings,
  topicClosed,
}: Omit<GetMemberActionsButtonProperties, 'isPO'>): DropdownData => {
  const leaveActionItems = !topicClosed
    ? [
        {
          ariaLabel: s__('Accessibility|Leave the group'),
          label: I18n.t('web.groups.unfollow'),
          onClick: () => unfollowTopic(),
        },
      ]
    : [];

  const actionsItems = [
    ...leaveActionItems,
    {
      label: s__('Topics|Notifications'),
      render: () => (
        <Box px={3} pt={2} pb={1} font="body-semibold-asphalt">
          {s__('Topics|Notifications')}
        </Box>
      ),
    },
    ...notificationOptions.map(({ ariaLabel, label, type }) =>
      buildOption({ ariaLabel, label, type, following, updateSettings }),
    ),
  ];

  return {
    items: actionsItems,
    title: s__('Accessibility|Group settings'),
    dataTestId: 'members-actions-dropdown',
  };
};

const isOptionActive = (following: Following, type) =>
  following.group_activity_frequency === type;

interface BuildOptionsProperties {
  ariaLabel?: string;
  following: Following;
  label: string;
  type: string;
  updateSettings: GroupHeaderButtonsProperties['updateSettings'];
}

const buildOption = ({
  ariaLabel,
  label,
  type,
  following,
  updateSettings,
}: BuildOptionsProperties) => ({
  ariaLabel,
  dataTestId: isOptionActive(following, type)
    ? 'member-action-selected'
    : 'member-action-not-selected',
  selected: isOptionActive(following, type),
  label,
  onClick: () => {
    updateSettings(following, {
      group_activity_frequency: type,
    });
  },
});
